import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { usePageMeta } from '../../lib/seo';
import { Alert, Button, Card, Field, Input, useToast } from '../../components/ui';
import { LogoMark } from '../../components/Logo';
import { tr } from '../../lib/i18n';

interface ResetPasswordResponse {
  ok: boolean;
}

export function ResetPasswordPage(): JSX.Element {
  usePageMeta(
    tr('Redefinir senha'),
    tr('Defina uma nova senha para sua conta AgendaPro. Seu negócio. Sua agenda. Seu tempo.'),
    '/redefinir-senha'
  );

  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);
  const [fields, setFields] = useState<{ token?: string; password?: string; confirm?: string }>({});

  // Pré-preenche o token vindo do link de e-mail (query string recebida), apenas para
  // conveniência. O token NUNCA é enviado em URL de navegação nem salvo em storage:
  // ele fica em memória e segue no corpo (body) da requisição.
  useEffect(() => {
    const fromLink = searchParams.get('token');
    if (fromLink) setToken(fromLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientErrors = useMemo(() => {
    const e: { token?: string; password?: string; confirm?: string } = {};
    if (token.trim().length < 10) e.token = tr('O token deve ter ao menos 10 caracteres.');
    if (password.length < 8) e.password = tr('A senha deve ter ao menos 8 caracteres.');
    if (confirm !== password) e.confirm = tr('As senhas não coincidem.');
    return e;
  }, [token, password, confirm]);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (Object.keys(clientErrors).length > 0) {
      setFields(clientErrors);
      setGeneral(null);
      return;
    }
    setSubmitting(true);
    setGeneral(null);
    setFields({});
    try {
      await api.post<ResetPasswordResponse>('/api/auth/reset-password', {
        token: token.trim(),
        password
      });
      toast(tr('Senha redefinida! Entre com a nova senha.'), 'success');
      // Navega sem qualquer token/senha na URL.
      navigate('/entrar', { replace: true, state: { resetDone: true } });
    } catch (err) {
      let fieldToken: string | undefined;
      let message: string | null = null;
      if (err instanceof ApiError) {
        if (err.code === 'invalid_reset_token') {
          fieldToken = err.message;
        } else {
          message = err.message || tr('Não foi possível redefinir a senha.');
        }
      } else {
        message = tr('Não foi possível redefinir a senha. Tente novamente.');
      }
      setFields({ token: fieldToken });
      setGeneral(message);
      if (message) toast(message, 'danger');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="mx-auto w-full max-w-content px-4 py-10 sm:py-14">
      <div className="mx-auto grid w-full max-w-md gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <LogoMark size={48} />
          <h1 className="text-3xl">{tr("Redefinir senha")}</h1>
          <p className="nl-hint">{tr("Seu negócio. Sua agenda. Seu tempo.")}</p>
        </div>

        <Card as="section">
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
            {general ? <Alert tone="danger" title={tr("Não foi possível redefinir")}>{general}</Alert> : null}

            <Alert tone="info" title={tr("Token de redefinição")}>
              <p>{tr("Cole abaixo o token que você recebeu. Ele é enviado apenas no corpo da requisição, nunca em URL.")}</p>
            </Alert>

            <Field label={tr("Token")} hint="Recebido por e-mail ou informado pelo suporte." error={fields.token} required>
              {(p) => (
                <Input
                  type="text"
                  autoComplete="one-time-code"
                  spellCheck={false}
                  placeholder={tr("Token de redefinição")}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  disabled={submitting}
                  invalid={Boolean(fields.token)}
                  {...p}
                />
              )}
            </Field>

            <Field label={tr("Nova senha")} hint="Mínimo de 8 caracteres." error={fields.password} required>
              {(p) => (
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  invalid={Boolean(fields.password)}
                  {...p}
                />
              )}
            </Field>

            <Field label={tr("Confirmar nova senha")} error={fields.confirm} required>
              {(p) => (
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={submitting}
                  invalid={Boolean(fields.confirm)}
                  {...p}
                />
              )}
            </Field>

            <Button type="submit" loading={submitting} disabled={submitting} className="w-full">
              {tr("Redefinir senha")}
            </Button>

            <p className="text-center text-sm text-muted">
              {tr("Precisa de um novo token?")}{' '}
              <Link to="/esqueci-senha" className="font-medium text-graphite hover:underline">
                {tr("Solicitar redefinição")}
              </Link>
            </p>
          </form>
        </Card>
      </div>
    </section>
  );
}
